import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { X, Loader2, AlertTriangle } from 'lucide-react';
import { adminTicketsAPI, adminLookupAPI } from '../../services/api';
import MemberDayTimeline from './MemberDayTimeline';

const DURATIONS = [
  { label: '15 min', minutes: 15 },
  { label: '30 min', minutes: 30 },
  { label: '1 hr', minutes: 60 },
  { label: '2 hr', minutes: 120 },
  { label: '4 hr', minutes: 240 },
];

// CW Reminders are a fixed lookup; the REST endpoint isn't exposed, so we
// stick to the IDs we've verified against this install.
const REMINDERS = [
  { label: 'None', minutes: null },
  { label: '10 min', minutes: 10 },
  { label: '15 min', minutes: 15 },
  { label: '1 hour', minutes: 60 },
];

function nextTopOfHourLocal() {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toLocalInput(iso) {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AdminScheduleEntryModal({
  ticketId,
  defaultMemberId = '',
  existing = null,
  onClose,
  onSuccess,
}) {
  const isEdit = !!existing;
  const [memberId, setMemberId] = useState(
    existing?.member?.id
      ? String(existing.member.id)
      : defaultMemberId
      ? String(defaultMemberId)
      : ''
  );
  const [start, setStart] = useState(
    existing?.dateStart ? toLocalInput(existing.dateStart) : nextTopOfHourLocal()
  );
  const [duration, setDuration] = useState(
    existing?.dateStart && existing?.dateEnd
      ? Math.max(
          15,
          Math.round((new Date(existing.dateEnd).getTime() - new Date(existing.dateStart).getTime()) / 60000)
        )
      : 60
  );
  const [reminder, setReminder] = useState(
    existing?.reminder?.id
      ? { 3: 10, 4: 15, 6: 60 }[existing.reminder.id] ?? null
      : 15
  );
  const [whereId, setWhereId] = useState(existing?.where?.id ? String(existing.where.id) : '');
  const [statusId, setStatusId] = useState(existing?.status?.id ? String(existing.status.id) : '');
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const { data: membersData } = useQuery({
    queryKey: ['admin-lookup-members'],
    queryFn: () => adminLookupAPI.members(),
  });
  const members = membersData?.data?.items || [];

  const { data: wheresData } = useQuery({
    queryKey: ['admin-lookup-wheres'],
    queryFn: () => adminLookupAPI.scheduleWheres(),
    staleTime: 10 * 60 * 1000,
  });
  const { data: statusesData } = useQuery({
    queryKey: ['admin-lookup-sched-statuses'],
    queryFn: () => adminLookupAPI.scheduleStatuses(),
    staleTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    if (!statusId && statusesData?.data?.items?.length) {
      const firm =
        statusesData.data.items.find((s) => /firm/i.test(s.name || '')) ||
        statusesData.data.items[0];
      setStatusId(String(firm.id));
    }
  }, [statusesData, statusId]);

  const startIso = useMemo(() => {
    const d = new Date(start);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }, [start]);

  const endIso = useMemo(() => {
    if (!startIso) return null;
    return new Date(new Date(startIso).getTime() + duration * 60_000).toISOString();
  }, [startIso, duration]);

  const dayWindow = useMemo(() => {
    if (!startIso) return null;
    const d = new Date(startIso);
    const from = new Date(d); from.setHours(0, 0, 0, 0);
    const to = new Date(d); to.setHours(23, 59, 59, 999);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [startIso]);

  const { data: memberScheduleData } = useQuery({
    queryKey: ['admin-member-schedule', memberId, dayWindow?.from, dayWindow?.to],
    queryFn: () => adminTicketsAPI.getMemberSchedule(memberId, dayWindow.from, dayWindow.to),
    enabled: !!memberId && !!dayWindow,
    staleTime: 30_000,
  });
  const memberSchedule = memberScheduleData?.data?.items || [];

  const conflicts = useMemo(() => {
    if (!startIso || !endIso) return [];
    const s = new Date(startIso).getTime();
    const e = new Date(endIso).getTime();
    return memberSchedule.filter((entry) => {
      const es = new Date(entry.dateStart).getTime();
      const ee = new Date(entry.dateEnd).getTime();
      return s < ee && es < e;
    });
  }, [memberSchedule, startIso, endIso]);

  const saveMutation = useMutation({
    mutationFn: (data) =>
      isEdit
        ? adminTicketsAPI.updateSchedule(ticketId, existing.id, data)
        : adminTicketsAPI.addSchedule(ticketId, data),
    onSuccess: () => {
      onSuccess?.();
      onClose?.();
    },
    onError: (err) =>
      setError(err.response?.data?.error || err.response?.data?.details || 'Failed to schedule'),
  });

  const submit = (allowConflicts = false) => {
    setError(null);
    if (!isEdit && conflicts.length > 0 && !allowConflicts) {
      setConfirming(true);
      return;
    }
    const payload = {
      memberId: parseInt(memberId),
      dateStart: startIso,
      dateEnd: endIso,
      reminderMinutes: reminder ?? null,
      whereId: whereId ? parseInt(whereId) : null,
      statusId: statusId ? parseInt(statusId) : null,
    };
    if (!isEdit) payload.allowScheduleConflictsFlag = allowConflicts;
    saveMutation.mutate(payload);
  };

  const canSubmit = !!memberId && !!startIso && !!endIso && !saveMutation.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-auto"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[95vh] overflow-auto">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            {isEdit ? 'Edit Schedule Entry' : 'Schedule Resource'}
          </h2>
          <button onClick={onClose} className="p-1 text-neutral-400 hover:text-neutral-700 dark:hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Member *">
              <Select
                value={memberId}
                onChange={setMemberId}
                options={members.map((m) => ({ value: String(m.id), label: m.name }))}
                placeholder="Pick a CW member…"
              />
            </Field>
            <Field label="Start *">
              <input
                type="datetime-local"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-sm"
              />
            </Field>
            <Field label="Duration">
              <Select
                value={String(duration)}
                onChange={(v) => setDuration(parseInt(v))}
                options={DURATIONS.map((d) => ({ value: String(d.minutes), label: d.label }))}
              />
            </Field>
            <Field label="Reminder">
              <Select
                value={reminder == null ? '' : String(reminder)}
                onChange={(v) => setReminder(v === '' ? null : parseInt(v))}
                options={REMINDERS.map((r) => ({
                  value: r.minutes == null ? '' : String(r.minutes),
                  label: r.label,
                }))}
                allowEmpty
              />
            </Field>
            <Field label="Location">
              <Select
                value={whereId}
                onChange={setWhereId}
                options={(wheresData?.data?.items || []).map((w) => ({
                  value: String(w.id),
                  label: w.name,
                }))}
                placeholder="Anywhere"
                allowEmpty
              />
            </Field>
            <Field label="Status">
              <Select
                value={statusId}
                onChange={setStatusId}
                options={(statusesData?.data?.items || []).map((s) => ({
                  value: String(s.id),
                  label: s.name,
                }))}
                placeholder="Status…"
              />
            </Field>
          </div>

          {memberId && startIso && (
            <MemberDayTimeline
              dayDate={new Date(startIso)}
              scheduleEntries={memberSchedule}
              proposed={{ dateStart: startIso, dateEnd: endIso }}
            />
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-neutral-200 dark:border-neutral-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700"
          >
            Cancel
          </button>
          <button
            disabled={!canSubmit}
            onClick={() => submit(false)}
            className="px-4 py-2 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 flex items-center gap-2"
          >
            {saveMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Schedule
          </button>
        </div>
      </div>

      {confirming && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-md w-full p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 mb-2">
              <AlertTriangle className="w-5 h-5" />
              <h3 className="font-semibold text-neutral-900 dark:text-white">Schedule conflict</h3>
            </div>
            <p className="text-sm text-neutral-700 dark:text-neutral-300">
              The proposed time overlaps with {conflicts.length} existing entr{conflicts.length === 1 ? 'y' : 'ies'}.
            </p>
            <ul className="mt-2 text-sm text-red-600 dark:text-red-400 list-disc list-inside max-h-32 overflow-auto">
              {conflicts.map((c) => (
                <li key={c.id}>
                  {c.summary || `${c.type || 'Entry'} #${c.objectId || ''}`} (
                  {new Date(c.dateStart).toLocaleTimeString()} – {new Date(c.dateEnd).toLocaleTimeString()})
                </li>
              ))}
            </ul>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setConfirming(false)} className="px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg">
                Pick a different time
              </button>
              <button
                onClick={() => { setConfirming(false); submit(true); }}
                className="px-3 py-2 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
              >
                Book anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function Select({ value, onChange, options, placeholder, allowEmpty }) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-sm"
    >
      {(placeholder || allowEmpty) && <option value="">{placeholder || ''}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
